<template>
  <div :dir="$ctx.getDir()">
    <el-drawer
        v-model="payDrawerOpen"
        v-if="payDrawerOpen"
        :direction="'rtl'"
        :lock-scroll="false"
        class="overflow-scroll"
    >
      <div class="px-5" style="margin-top: -20px !important">
        <multi-currency-payment-input
            v-model:total-paid="collectivePayment.customerInvoicePayment.totalPaidAmount"
            v-model:change-due="collectivePayment.customerInvoicePayment.changeDueAmount"
            v-model:total-paid-in-cash-before-change="collectivePayment.customerInvoicePayment.reqOnlyTotalCashPaidAmount"
            v-model:total-paid-in-credit="collectivePayment.customerInvoicePayment.reqOnlyTotalCreditPaidAmount"
            v-model:total-paid-in-epayment="collectivePayment.customerInvoicePayment.reqOnlyTotalEpaymentAmount"
            v-model:cash-multi-currency-breakdown="collectivePayment.customerInvoicePayment.reqOnlyCashMultiCurrencyBreakdown"
            :total-due="collectivePayment.customerInvoicePayment.totalDueAmount"
            :customerID="customerID"
        />

        <div class="mt-4">
          <div>{{ t('notes') }}</div>
          <div>
            <el-input v-model="collectivePayment.customerInvoicePayment.notes" :rows="2" type="textarea" placeholder="" />
          </div>
        </div>

        <div class="mt-4">
          <div>{{ t('moneyCollector') }}</div>
          <div>
            <el-select
              v-model="collectivePayment.customerInvoicePayment.moneyCollectorID"
              class="w-full"
              filterable
              clearable
              :placeholder="t('selectMoneyCollector')"
              :teleported="false"
            >
              <el-option
                v-for="collector in moneyCollectors"
                :key="collector.id"
                :label="collector.name"
                :value="collector.id"
              />
            </el-select>
          </div>
        </div>

        <div class="flex items-center justify-between my-2">
          <horizontal-button
              class="text-lg complete-payment"
              :title="t('buttons.completePay')"
              @click="openCashierPinDialog()"
              :rounded="false"
          />
        </div>
      </div>
    </el-drawer>
    <el-dialog v-model="cashierPinDialogOpen" width="30%">
      <div>
        <!-- Enter the pin -->
        <div class="my-2">
          <p>{{ t('enterCashierPin') }}</p>
        </div>

        <!-- password input up to 6 chars -->
        <div class="my-2">
          <el-input
              id="password"
              v-model="cashierPin"
              type="password"
              required
              show-password
              autofocus
              dir="ltr"
              :maxlength="4"
              :minlength="4"
              @keyup.enter="submitCollectivePayment()"
              ref="cashierPinInput"
          />
        </div>

        <div class="flex items-center justify-between mb-2">
          <horizontal-button
              class="text-lg complete-payment"
              :title="t('buttons.confirm')"
              @click="submitCollectivePayment()"
              :rounded="false"
          />
        </div>
      </div>
    </el-dialog>
    <alert-badge
        v-if="error.title"
        isDanger
        :title="error.title"
        :body="error.body"
        @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <template v-if="$can('read', '/sales/orders')">
        <el-select class="input-label col-span-12 md:col-span-2" v-model="selectedValue">
          <el-option
              key="quickView"
              :label="$t('views.common.listView.quickView')"
              value="quickView"
          >
          </el-option>
          <el-option key="allItems" :label="$t('views.common.listView.allItems')" value="allItems">
          </el-option>
          <el-option
              key="export"
              :label="$t('views.common.listView.selectedRows.export')"
              value="export"
          >
          </el-option>
          <!-- <template v-if="$can('delete', '/sales/orders')">
            <el-option
              key="delete"
              :label="$t('views.common.listView.selectedRows.delete')"
              value="delete"
            >
            </el-option>
          </template> -->
        </el-select>
      </template>

      <el-input
          :placeholder="t('searchReceipt')"
          class="w-full col-span-12 md:col-span-10"
          :prefix-icon="Search"
          v-model="searchTerm"
          ref="searchBar"
      />

    </div>

    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <div>
          <el-date-picker
              v-model="range"
              class="w-full"
              type="daterange"
              range-separator="to"
              :start-placeholder="t('startDate')"
              :end-placeholder="t('endDate')"
              @change="onFilterChange"
              :format="'DD-MM-YYYY'"

          >
          </el-date-picker>
        </div>
        <div class="flex gap-2">
          <ToggleGroup v-model="unpaidFilter" class="gap-2 flex-wrap lg:flex-nowrap" type="single">
            <ToggleGroupItem value="unpaid" class="data-[state=on]:bg-yellow-500/30 data-[state=on]:border-gray-500 data-[state=on]:text-gray-800 bg-gray-100">
              {{t('unpaid')}}
            </ToggleGroupItem>
          </ToggleGroup>
          <el-button round @click="selectAll()">{{t('selectAll')}}</el-button>
        </div>
      </div>
      <div v-if="selectedOrders.length" class="flex items-center gap-4">
        <div class="flex items-center gap-2">
          <div>{{t('total')}}</div>
          <div class="font-bold">{{formatMoney(collectivePayment?.customerInvoicePayment.totalDueAmount, "IQD")}}</div>
        </div>
        <el-button class="w-[200px]" type="primary" @click="() => {openPaymentDrawer()}">
          {{t('buttons.collectivePayment')}}
        </el-button>
      </div>
    </div>

    <div class="my-2 lg:mb-2 lg:w-2/3">
      <ToggleGroup v-model="orderTypeFilter" class="gap-2 flex-wrap lg:flex-nowrap" type="multiple">
        <ToggleGroupItem value="unprocessed" class="data-[state=on]:bg-white data-[state=on]:border-[#FCC5C5] data-[state=on]:text-gray-800 hover:bg-[#FCC5C5] bg-[#FCC5C5]">
          {{t('orderStatus.unprocessed')}}
        </ToggleGroupItem>
        <ToggleGroupItem value="processing" class="data-[state=on]:bg-white data-[state=on]:border-[#FFE072] data-[state=on]:text-gray-800 hover:bg-[#FFE072] bg-[#FFE072]">
          {{t('orderStatus.processing')}}
        </ToggleGroupItem>
        <ToggleGroupItem value="ready" class="data-[state=on]:bg-white data-[state=on]:border-[#B8DAFC] data-[state=on]:text-gray-800 hover:bg-[#B8DAFC] bg-[#B8DAFC]">
          {{t('orderStatus.ready')}}
        </ToggleGroupItem>
        <ToggleGroupItem value="on_the_way" class="data-[state=on]:bg-white data-[state=on]:border-[#D0C5F4] data-[state=on]:text-gray-800 hover:bg-[#D0C5F4] bg-[#D0C5F4]">
          {{t('orderStatus.onTheWay')}}
        </ToggleGroupItem>
        <ToggleGroupItem value="delivered" class="data-[state=on]:bg-white data-[state=on]:border-[#D1F1BB] data-[state=on]:text-gray-800 hover:bg-[#D1F1BB] bg-[#D1F1BB]">
          {{t('orderStatus.delivered')}}
        </ToggleGroupItem>
      </ToggleGroup>
    </div>

    <ag-grid-vue
        v-if="rowModelType"
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 50vh"
        @cell-double-clicked="onCellDoubleClicked"
        @selection-changed="onSelectionChange"
        :defaultColDef="defaultColDef"
        :rowData="rowData"
        :pagination="selectedValue == 'allItems'"
        :paginationAutoPageSize="selectedValue == 'allItems'"
        :onGridReady="onGridReady"
        :components="components"
        :rowBuffer="rowBuffer"
        :rowModelType="rowModelType"
        :cacheBlockSize="cacheBlockSize"
        :cacheOverflowSize="cacheOverflowSize"
        :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
        :infiniteInitialRowCount="infiniteInitialRowCount"
        :maxBlocksInCache="maxBlocksInCache"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import {defineComponent, nextTick, ref} from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import {
  ColDef,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  ValueFormatterParams,
  ModuleRegistry
} from 'ag-grid-community'

import { i18nOrderMessages, Order, OrdersPaginated } from '@/models/sales/Order'
import { useI18n } from 'vue-i18n'
import { logError } from '@/utils/log'
import { formatPostgresDate } from '@/utils/date'
import { formatMoney } from '@/utils/money'
import { Search } from '@element-plus/icons-vue'

import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model'
import { onBeforeMount } from 'vue'
import OrderStatusCellRenderer from "@/views/sales/OrderStatusCellRenderer.vue";
import PaymentStatusCellRenderer
  from "@/views/sales/PaymentStatusCellRenderer.vue";
ModuleRegistry.registerModules([InfiniteRowModelModule])
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import Customer from "@/models/customer/Customer";
import HorizontalButton from "@/components/HorizontalButton.vue";
import {ElInput, ElMessage} from "element-plus";
import MultiCurrencyPaymentInput
  from "@/components/money/MultiCurrencyPaymentInput.vue";
import {CustomerInvoicePayment} from "@/models/sales/CustomerInvoice";
import {useSettingsStore} from "@/stores/settingsStore";
import {Transaction} from "@/models/accounting/Transaction";
import {Route} from "@/router/routes";
import {useAlertModal} from "@/plugins/alert-modal/alert-modal";
import {useHTTP} from "@/plugins/http";
import AlertBadge from "@/components/AlertBadge.vue";
import dayjs from "dayjs";
import InsurancePaymentStatusCellRenderer
  from "@/views/companies/insurances/InsurancePaymentStatusCellRenderer.vue";
import User from '@/models/user/User';

export default defineComponent({
  name: 'orders',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...i18nOrderMessages.en,
          searchReceipt: 'Enter receipt number',
          buttons: {
            collectivePayment: 'Collective Pay',
            pay: 'Pay',
            completePay: 'Process Payment',
            confirm: "Confirm"
          },
          notes: "Notes",
          moneyCollector: 'Money Collector',
          selectMoneyCollector: 'Select money collector',
          enterCashierPin: 'Enter cashier pin',
          errCashierPinRequired: 'Cashier pin is required',
          successMsg: 'Payment done',
          errInvalidAmount: 'Invalid amount. Must be bigger > 0',
          errPaymentIsComplete: 'Payments are already complete, nothing to pay',
          unpaid: "Unpaid only",
          noPaymentIsRequired: "No payment is required",
          selectAll: 'Select All',
        },
        ar: {
          ...i18nOrderMessages.ar, searchReceipt: 'ادخل رقم الفاتورة',
          buttons: {
            collectivePayment: 'دفع جماعي',
            pay: 'دفع',
            completePay: 'اتمام الدفع',
            confirm: "تأكيد"
          },
          notes: "الملاحظات",
          moneyCollector: 'جابي الأموال',
          selectMoneyCollector: 'اختر جابي الأموال',
          enterCashierPin: 'ادخل رمز الكاشير الخاص بك',
          errCashierPinRequired: 'يرجى ادخال رمز الكاشير لاتمام الدفع',
          successMsg: 'تم اكمال الدفع',
          errInvalidAmount: 'يجب ان يكون المبلغ المدفوع اكبر من 0',
          errPaymentIsComplete: 'تم دفع المبلغ كاملا',
          unpaid: "غير مكتمل الدفع",
          noPaymentIsRequired: "لا يتطلب الدفع",
          selectAll: 'تحديد الجميع'
        }
      }
    })

    const error = ref({ title: '', body: '' })
    const rowBuffer = ref<any>(null)
    const rowSelection = ref<any>(null)
    const rowModelType = ref<any>(null)
    const cacheBlockSize = ref<any>(null)
    const cacheOverflowSize = ref<any>(null)
    const maxConcurrentDatasourceRequests = ref<any>(null)
    const infiniteInitialRowCount = ref<any>(null)
    const maxBlocksInCache = ref<any>(null)
    const orderTypeFilter = ref<Array<string>>([])
    const unpaidFilter = ref<string|undefined>()
    const range = ref<Date[]>([])
    const selectedOrders = ref<Array<Order>>([]);
    const payDrawerOpen = ref(false)
    const collectivePayment = ref<{
      orderIDs: string[],
      customerInvoicePayment: CustomerInvoicePayment
    }>({
      orderIDs: [],
      customerInvoicePayment: new CustomerInvoicePayment()
    })
    const cashierPin = ref('')
    const cashierPinInput = ref()
    const cashierPinDialogOpen = ref(false)
    const moneyCollectors = ref<User[]>([])
    const http = useHTTP()


    onBeforeMount(async () => {
      rowBuffer.value = 0
      rowSelection.value = 'multiple'
      rowModelType.value = 'infinite'
      cacheBlockSize.value = 100
      cacheOverflowSize.value = 100
      maxConcurrentDatasourceRequests.value = 2
      infiniteInitialRowCount.value = 0
      maxBlocksInCache.value = 10

      // Load money collectors (users with "Money Collector" tag)
      try {
        const usersRes = await http.get<User[]>(User.ENDPOINT)
        moneyCollectors.value = usersRes
          .map(user => User.from(user))
          .filter(user => user.extraProperties?.tags?.includes('Money Collector'))
      } catch (err) {
        console.error('Failed to load money collectors', err)
      }
    })

    function onSelectionChange(e: any) {
      selectedOrders.value = e.api.getSelectedRows()
      collectivePayment.value.customerInvoicePayment.changeDueAmount = 0
      collectivePayment.value.customerInvoicePayment.paidBeforeChangeAmount = 0
      collectivePayment.value.orderIDs = selectedOrders.value.map(o => o.id);
      collectivePayment.value.customerInvoicePayment.totalDueAmount = selectedOrders.value.reduce((a, c) => a + c.invoice.totalBalanceAmount, 0);
    }

    return {
      t,
      Search,
      rowBuffer,
      rowSelection,
      rowModelType,
      cacheBlockSize,
      cacheOverflowSize,
      maxConcurrentDatasourceRequests,
      infiniteInitialRowCount,
      maxBlocksInCache,
      orderTypeFilter,
      range,
      selectedOrders,
      payDrawerOpen,
      collectivePayment,
      cashierPin,
      cashierPinInput,
      cashierPinDialogOpen,
      onSelectionChange,
      error,
      unpaidFilter,
      moneyCollectors
    }
  },
  data() {
    const columnDefs: ColDef[] = []
    const rowData: Order[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}
    const datasource: IDatasource = {} as IDatasource
    const components = {
      loadingRenderer: (params: ValueFormatterParams) => {
        return params.value !== undefined
            ? params.value
            : '<img src="https://www.ag-grid.com/example-assets/loading.gif">'
      }
    }

    return {
      dateRange: '',
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,
      components,
      datasource,
      searchTerm: '',
      selectedValue: 'quickView',
      gridApi: undefined as GridApi | undefined,
    }
  },

  components: {
    AlertBadge,
    MultiCurrencyPaymentInput, ElInput, HorizontalButton,
    AgGridVue,
    ToggleGroup,
    ToggleGroupItem
  },

  props: {
    customerID: {
      type: String,
      required: true
    }
  },

  async beforeMount() {
    this.defaultColDef = {
      resizable: true,
      minWidth: 50,
      sortable: false,
      filter: false
    }

    this.columnDefs = [
      {
        field: '-',
        hide: true,
        lockVisible: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      },

      {
        headerName: this.t('orderNumber'),
        field: 'externalID',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 140
      },
      {
        headerName: this.$t('models.common.date'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (order.createdAt) {
            const createdAt = new Date(order.createdAt)
            return formatPostgresDate(String(createdAt), 'DD/MM/YYYY')
          }
          return ''
        },
        width: 140
      },
      {
        headerName: this.$t('models.common.time'),
        field: 'createdAt',
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (order.createdAt) {
            const createdAt = new Date(order.createdAt)
            return formatPostgresDate(String(createdAt), 'hh:mm A')
          }
          return ''
        },
        width: 100
      },
      {
        headerName: this.t('orderType'),
        width: 100,
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          switch (order.orderType) {
            case 'direct':
              return this.t('orderTypes.direct');
            case 'pick_up':
              return this.t('orderTypes.pickUp');
            case 'delivery':
              return this.t('orderTypes.delivery');
          }
        },
      },
      {
        headerName: this.t('orderState'),
        cellRenderer: OrderStatusCellRenderer,
        cellRendererParams: {
          // handleCheckChange: handleCheckChange
        }
      },
      {
        headerName: this.t('customerPaymentState'),
        cellRenderer: PaymentStatusCellRenderer,
      },
      {
        headerName: this.t('paymentTerms'),
        width: 150,
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          switch (order.invoice.paymentType) {
            case 'direct':
              return this.t('invoiceTypes.full');
            case 'layaway':
              return this.t('invoiceTypes.layaway');
            case 'credit_sale':
              return this.t('invoiceTypes.credit');
            case 'cash_on_delivery':
              return this.t('invoiceTypes.cashOnDelivery');
          }
        },
      },
      {
        headerName: this.t('insurancePaymentState'),
        cellRenderer: InsurancePaymentStatusCellRenderer,
      },
      {
        headerName: this.t('total'),
        field: 'invoice.totalDueAmount',
        valueGetter: ({ data }) => {
          const order = data as Order
          if (!order) return ''
          return order.invoice?.totalDueAmount != undefined
              ? formatMoney(order.invoice?.totalDueAmount + order.invoice?.insuranceTotalDueAmount, order.invoice.currency)
              : ''
        }
      },
      {
        headerName: this.t('customerPaid'),
        field: 'invoice.totalPaidAmount',
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''

          return formatMoney(order.invoice.totalPaidAmount ?? 0, order.invoice.currency)
        }
      },
      {
        headerName: this.t('insurancePaid'),
        field: 'invoice.totalPaidAmount',
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''

          return formatMoney(order.invoice.insuranceTotalPaidAmount ?? 0, order.invoice.currency)
        }
      },
      {
        headerName: this.t('balance'),
        field: 'invoice.totalBalanceAmount',
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          return formatMoney(order.invoice.totalBalanceAmount + order.invoice?.insuranceTotalBalanceAmount ?? 0, order.invoice.currency)
        }
      },
      {
        headerName: this.t('user'),
        field: 'user.fullName',
        cellStyle: { textAlign: 'center' }
      },
      {
        headerName: this.t('salesRep'),
        field: 'invoice.salesRep.fullName',
        cellStyle: { textAlign: 'center' },
        valueGetter: (params) => {
          const order = params.node?.data as Order
          if (!order) return ''
          if (!order.invoice.salesRep) return '';
          return `${order.invoice.salesRep.name}`
        }
      },
      {
        headerName: this.t('customer'),
        field: 'invoice.customer.contactDisplayName',
        cellStyle: { textAlign: 'center' }
      },
      // {
      //   headerName: this.t('isDelivered'),
      //   field: 'isDelivered',
      //   valueFormatter: ({ data }) => {
      //     const order = data as Order
      //     if (!order) return ''
      //     return order.isDelivered ? '✔' : '✖'
      //   },
      //   cellStyle: (params) => {
      //     const base = { textAlign: 'center', backgroundColor: 'transparent', color: 'black' }
      //     const order = params.node?.data as Order
      //     if (!order) return base
      //
      //     if (order.isDelivered) {
      //       base['color'] = 'green'
      //     } else {
      //       base['color'] = 'red'
      //     }
      //
      //     return base
      //   }
      // },
      {
        headerName: this.t('pouchID'),
        field: 'invoice.pouchID',
        hide: true,
        cellStyle: { textAlign: 'center' }
      }
    ]
  },

  mounted() {
    this.gridApi?.sizeColumnsToFit()
  },

  watch: {
    orderTypeFilter: {
      handler() {
        this.updateOrderStatusFilter();
      },
    },
    unpaidFilter: {
      handler() {
        this.updateOrderStatusFilter();
      },
    },
    range: {
      handler() {
        this.updateOrderStatusFilter();
      },
    },
    searchTerm() {
      this.filterItems()
    },
    async selectedValue(newValue, oldValue) {
      if (oldValue == 'delete' || oldValue == 'export') {
        return
      }

      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected)
          } else {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
        this.selectedValue = oldValue
      } else if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    }
  },

  methods: {
    formatMoney,
    updateOrderStatusFilter() {
      this.selectedOrders = [];

      if (this.selectedValue == 'allItems') {
        this.rowModelType = null
        this.defaultColDef.sortable = true
        this.defaultColDef.filter = true
        setTimeout(() => {
          this.rowModelType = 'clientSide'
        }, 100)
      } else if (this.selectedValue == 'quickView') {
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
        }, 100)
      }
    },
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()

      const urlParams = new URLSearchParams()
      urlParams.set('customer_id', this.$route.params.id)
      if (this.orderTypeFilter.length) {
        urlParams.set('order_status', this.orderTypeFilter.join(','))
      }

      if (this.unpaidFilter) {
        urlParams.set('payment_status', 'unpaid')
      }

      if (this.range.length) {
        urlParams.set('from_date', dayjs(this.range[0]).format('YYYY-MM-DD'))
        urlParams.set('to_date', dayjs(this.range[1]).format('YYYY-MM-DD'))
      }

      try {
        const data = await this.$http.get<Order[]>(Order.ENDPOINT + `?${urlParams.toString()}`)
        this.rowData = data.map((order) => Order.from(order))
        this.gridApi?.setGridOption('rowData', this.rowData)
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }
      this.gridApi?.hideOverlay()
    },

    getDatasource() {
      const dataSource = {
        rowCount: undefined,
        getRows: async (params: IGetRowsParams) => {
          this.gridApi?.showLoadingOverlay()

          const urlParams = new URLSearchParams()
          urlParams.set('customer_id', this.$route.params.id)
          if (this.orderTypeFilter.length) {
            urlParams.set('order_status', this.orderTypeFilter.join(','))
          }

          if (this.unpaidFilter) {
            urlParams.set('payment_status', 'unpaid')
          }

          if (this.range.length) {
            urlParams.set('from_date', dayjs(this.range[0]).format('YYYY-MM-DD'))
            urlParams.set('to_date', dayjs(this.range[1]).format('YYYY-MM-DD'))
          }

          let url = `${Order.ENDPOINT}?startRow=${params.startRow}&endRow=${params.endRow}&${urlParams.toString()}`
          if (params.sortModel.length) {
            url += `&colId=${params.sortModel[0].colId}&sort=${params.sortModel[0].sort}`
          }

          if (Object.keys(params.filterModel).length) {
            url += `&query=${params.filterModel['-'].filter}`
          }

          const res = await this.$http.get<OrdersPaginated>(url)
          this.rowData = res.data.map((order) => Order.from(order))
          this.gridApi?.hideOverlay()

          const lastRow = res.total
          // call the success callback
          params.successCallback(this.rowData, lastRow)
        }
      }
      return dataSource
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      if (this.selectedValue == 'quickView') {
        this.gridApi?.setGridOption('datasource', this.getDatasource())
        this.gridApi?.setGridOption('serverSideSortOnServer', true)
      } else if (this.selectedValue == 'allItems') {
        this.gridApi?.setGridOption('serverSideSortOnServer', false)
        this.updateRowData()
      }
    },

    filterItems() {
      if (this.rowModelType == 'infinite') {
        this.gridApi?.setFilterModel({ '-': { filter: this.searchTerm } })
        return
      }
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    async delete(orders: Order[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(Order.ENDPOINT, { data: orders })
          await this.updateRowData()
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },
    onCellDoubleClicked(event: any) {
      this.$router.push(`/sales/orders/order/${event.data.id}`)
    },

    async openCashierPinDialog() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        this.cashierPinDialogOpen = true

        nextTick(() => {
          setTimeout(() => {
            this.cashierPinInput.focus()
          }, 0)
        })
        return;
      }
      this.submitCollectivePayment()
    },

    async submitCollectivePayment() {
      if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
        if (this.cashierPin === '') {
          this.cashierPinDialogOpen = false
          this.error.title = this.t('errCashierPinRequired')
          return
        }
      }

      this.collectivePayment.customerInvoicePayment.totalPaidAmount =
          this.collectivePayment.customerInvoicePayment.reqOnlyTotalCashPaidAmount +
          this.collectivePayment.customerInvoicePayment.reqOnlyTotalEpaymentAmount +
          this.collectivePayment.customerInvoicePayment.reqOnlyTotalCreditPaidAmount

      if (
          this.collectivePayment.customerInvoicePayment.reqOnlyTotalCashPaidAmount <= 0 &&
          this.collectivePayment.customerInvoicePayment.reqOnlyTotalEpaymentAmount <= 0 &&
          this.collectivePayment.customerInvoicePayment.reqOnlyTotalCreditPaidAmount <= 0
      ) {
        this.cashierPinDialogOpen = false
        this.error.title = this.t('validation.inputErrors')
        this.error.body = this.t('errInvalidAmount')
        return
      }
      try {
        const resp = await this.$http.post<any>(
            `${CustomerInvoicePayment.ENDPOINT}/collective-payment`,
            this.collectivePayment,
            { headers: { pin: this.cashierPin } }
        )

        this.$alertModal.showSuccess({
          title: this.t('successMsg')
        })

        this.cashierPinDialogOpen = false
        this.payDrawerOpen = false
        window.scrollTo(0, 0)

        // reload table
        this.rowModelType = null
        this.defaultColDef.sortable = false
        this.defaultColDef.filter = false
        setTimeout(() => {
          this.rowModelType = 'infinite'
          this.selectedOrders = [];
        }, 100)
        this.$emit('collectivePayment')

      } catch (err: any) {
        this.error.title = err.title
        this.error.body = err.body
        this.cashierPinDialogOpen = false
      }
    },
    openPaymentDrawer() {
      if (this.collectivePayment?.customerInvoicePayment.totalDueAmount == 0) {
        ElMessage({
          message: this.t('noPaymentIsRequired'),
          type: 'warning'
        })
        return;
      }
      this.payDrawerOpen = true;
    },
    selectAll() {
      this.gridApi?.forEachNode((node) => {
        node.setSelected(true);
      });
    }
  }
})
</script>

<style></style>
